import Sidebar from '../components/Sidebar';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logoamerisa from '../assets/logoamerisa.png';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { Download, ChevronLeft, ChevronRight, Truck, MapPin, Clock } from 'lucide-react';
import axios from 'axios';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '../styles/carousel-override.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Tooltip } from 'react-tooltip';

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

const TrackingTimeline = ({ events, showAllEvents, toggleShowAllEvents, lastLongitude, lastLatitude, lastLocationText, eta, loadingEta }) => {
  const eventsToShow = showAllEvents ? events : events.slice(-6);

  // Función para determinar el estado de la entrega basado en ETA y cita
  function getDeliveryStatus(etaMinutes, appointmentDate, appointmentHr) {
    if (!etaMinutes || !appointmentDate) return null;
    
    // Convertir ETA a milisegundos (desde el momento actual)
    const etaMs = Date.now() + (etaMinutes * 60 * 1000);
    const etaDate = new Date(etaMs);

    // Convertir appointment a Date
    const [hours, minutes] = (appointmentHr || '00:00').split(':');
    const appointmentDateTime = new Date(appointmentDate);
    appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Calcular diferencia en minutos
    const diffMs = appointmentDateTime - etaDate;
    const diffMinutes = Math.round(diffMs / (1000 * 60));

    if (diffMinutes > 30) { // Más de 30 minutos antes
      return { status: 'En tiempo', color: 'text-green-600' };
    } else if (diffMinutes >= -30) { // Entre 30 minutos antes y 30 minutos después
      return { status: 'Posible retraso', color: 'text-amber-600' };
    } else { // Más de 30 minutos después
      return { status: 'Retrasado', color: 'text-red-600' };
    }
  }

  // Función para formatear la ETA en horas y minutos
  function formatEtaMinutes(etaMin) {
    if (!etaMin || etaMin <= 0) return '';
    const hours = Math.floor(etaMin / 60);
    const minutes = etaMin % 60;
    if (hours > 0) {
      return `${hours} h${minutes > 0 ? ` ${minutes} min` : ''}`;
    }
    return `${minutes} min`;
  }

  return (
    <div className="relative">
      <button
        onClick={toggleShowAllEvents}
        className="ml-4 md:ml-8 mb-3 md:mb-4 bg-blue-600 text-white p-1.5 md:p-2 rounded-md hover:bg-blue-700 text-xs md:text-sm"
      >
        {showAllEvents ? 'Mostrar menos' : 'Mostrar todos'}
      </button>
      <div className="absolute left-2 md:left-5 top-0 h-full w-0.5 bg-gray-300" />
      <div className="space-y-6 md:space-y-8 pl-5 md:pl-8">
        {eventsToShow.map((event, index) => (
          <div key={index} className="relative flex items-start">
            <div className="absolute left-0 w-3 md:w-4 h-3 md:h-4 bg-blue-500 rounded-full transform -translate-x-1/2" />
            <div className="ml-4 md:ml-6">
              <div className="flex items-center">
                <p className="text-sm md:text-lg font-semibold text-gray-800 flex items-center m-0">
                  {event.action}
                  {event.action === 'Orden en ruta' && (
                    <span className="flex items-center ml-2">
                      {/* Mostrar ETA o GPS solo si no ha llegado al cliente */}
                      {(!eventsToShow.some(e => e.action === 'Llegada al cliente' && e.rawDate)) ? (
                        !lastLatitude || !lastLongitude ? (
                          <span className="flex items-center">
                            <MapPin size={18} className="text-gray-500 animate-bounce ml-2" />
                            <span className="text-xs text-gray-600 font-semibold ml-2">Esperando ubicación GPS...</span>
                          </span>
                        ) : eta ? (
                          <>
                            <Clock size={18} className="text-amber-600 animate-pulse ml-2" />
                            <span className="text-xs text-amber-700 font-semibold ml-2">ETA: {formatEtaMinutes(eta)}</span>
                            {event.appointment && event.appointment_hr && (
                              <span className={`text-xs font-semibold ml-2 ${getDeliveryStatus(eta, event.appointment, event.appointment_hr)?.color}`}>
                                ({getDeliveryStatus(eta, event.appointment, event.appointment_hr)?.status})
                              </span>
                            )}
                          </>
                        ) : loadingEta ? (
                          <span className="flex items-center">
                            <Clock size={18} className="text-blue-600 animate-pulse ml-2" />
                            <span className="text-xs text-blue-700 font-semibold ml-2">Calculando ETA...</span>
                          </span>
                        ) : null
                      ) : null}
                    </span>
                  )}
                </p>
              </div>
              {event.details && <p className="text-xs md:text-sm text-gray-500 mt-1">{event.details}</p>}
              <p className="text-xs md:text-sm text-gray-500 mt-1">{event.timestamp}</p>
            </div>
          </div>
        ))}
      </div>      <style>{`
        @keyframes bounce-horizontal {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(10px); }
        }
        .animate-bounce-horizontal {
          animation: bounce-horizontal 1s infinite;
        }
        .animate-pulse {
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-bounce {
          animation: bounce 1s infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-25%); }
        }
      `}</style>
    </div>
  );
};

export const formatDate = (dateString) => {
  if (!dateString) return "Pendiente";
  
  try {
    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Pendiente";
    }
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return date.toLocaleDateString(undefined, options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Pendiente";
  }
};

const ORDER_STATUS = {
  PENDING: 'PENDIENTE',
  ARRIVAL: 'ARRIBADO',
  IN_TRANSIT: 'TRANSITO',
  DELIVERED: 'ENTREGADO',
  CANCELLED: 'CANCELADO',
  ON_CLIENT: 'EN CLIENTE',
  CREATED: 'CREADO',
  CLOSED: 'FINALIZADO',
};
const VALIDATION_ELIGIBLE_STATUSES = [ORDER_STATUS.DELIVERED, ORDER_STATUS.CLOSED];

const Cliente = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('OrderTracker');
  const [searchTerm, setSearchTerm] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [evidenceType, setEvidenceType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [validationInfo, setValidationInfo] = useState(null);
  const [eta, setEta] = useState(null);
  const [loadingEta, setLoadingEta] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  // Function to fetch order details
  const fetchOrderDetails = async (orderNumber) => {
    if (!orderNumber) return;
    
    setIsLoading(true);
    try {
      // First, make sure we have products data
      let productsData = products;
      if (productsData.length === 0) {
        try {
          const productsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/productos`);
          productsData = productsResponse.data;
          setProducts(productsData);
        } catch (error) {
          console.error('Error fetching Products:', error);
        }
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/orders/search-client/?do=${orderNumber}`);
      const data = await response.json();
      
      if (data.length > 0) {
        const order = data[0];
        
        // Procesar rechazos desde la nueva tabla de rechazos
        let rejections = [];
        let totalPiezasRechazadas = 0;
        let totalPiezasRechazadasTodas = 0;
        if (order.rechazos && Array.isArray(order.rechazos)) {
          rejections = order.rechazos.map((rechazo) => {
            const productInfo = productsData.find((p) => p.id === rechazo.product_id);
            // Sumar todas las piezas rechazadas
            totalPiezasRechazadasTodas += rechazo.cantidad;
            // Solo sumar si la operación no es 'FALTANTE'
            if (rechazo.operacion !== 'FALTANTE') {
              totalPiezasRechazadas += rechazo.cantidad;
            }
            return {
              product_id: rechazo.product_id,
              codigo_producto: productInfo ? productInfo.codigo_producto : 'Código no disponible',
              descripcion: productInfo ? productInfo.descripcion : 'Descripción no disponible',
              reason: rechazo.motivo || 'Sin razón especificada',
              operacion: rechazo.operacion || 'No especificada',
              date: order.metrics.order_Delivery || 'Fecha no disponible',
              pieces: rechazo.cantidad,
            };
          });
        }

        // Process shipment data
        const shipments = [];
        if (order.shipment) {
          shipments.push({
            ...order.shipment,
            photos_drive_ids: Array.isArray(order.shipment.photos_drive_ids) 
              ? order.shipment.photos_drive_ids 
              : []
          });
        }
        
        // Create tracking events timeline
        const trackingEvents = [
          { action: 'Orden Creada', timestamp: formatDate(order.metrics.order_Createdt), rawDate: order.metrics.order_Createdt },
          { action: 'Se agrupo la orden', timestamp: formatDate(order.metrics.order_Agroupdt), rawDate: order.metrics.order_Agroupdt, details: `Embarque: ${order.metrics.shipment_number}` },
          { action: 'Llegada a origen de carga', timestamp: formatDate(order.metrics.order_OriginATA), rawDate: order.metrics.order_OriginATA },
          { action: 'Inicio de carga', timestamp: formatDate(order.metrics.order_Origin_chargeinit), rawDate: order.metrics.order_Origin_chargeinit },
          { action: 'Fin de carga', timestamp: formatDate(order.metrics.order_Origin_chargefin), rawDate: order.metrics.order_Origin_chargefin },
          { action: 'Orden en ruta', timestamp: formatDate(order.metrics.order_OriginATD), rawDate: order.metrics.order_OriginATD, appointment: order.Appointment, appointment_hr: order.Appointment_hr },
          { action: 'Llegada al cliente', timestamp: formatDate(order.metrics.order_ClientATA), rawDate: order.metrics.order_ClientATA, details: ` ${order.City}, ${order.State}`},
          { action: 'Inicio de descarga', timestamp: formatDate(order.metrics.order_Clientramp), rawDate: order.metrics.order_Clientramp },
          { action: 'Fin de entrega', timestamp: formatDate(order.metrics.order_Delivery), rawDate: order.metrics.order_Delivery, details: `Estatus: ${order.order_Substatus}` },
          { action: 'Reintentando Entrega', timestamp: formatDate(order.metrics.reentregaATA), rawDate: order.metrics.reentregaATA },
          { action: 'Inicio de descarga', timestamp: formatDate(order.metrics.reentregaRamp), rawDate: order.metrics.reentregaRamp },
          { action: 'Fin de entrega', timestamp: formatDate(order.metrics.reentrega_dt), rawDate: order.metrics.reentrega_dt, details: `Estatus: ${order.substatus_reentrega}` },
          { action: 'En ruta a devolucion', timestamp: formatDate(order.metrics.dev_route_dt), rawDate: order.metrics.dev_route_dt, details: `Devolviendo a : ${order.bodega_dev}` },
          { action: 'Devolucion en bodega', timestamp: formatDate(order.metrics.devolucion_dt), rawDate: order.metrics.devolucion_dt, details: `Piezas Devueltas: ${totalPiezasRechazadasTodas}` },
          { action: 'Hora del POD', timestamp: formatDate(order.metrics.order_Podtime), rawDate: order.metrics.order_Podtime },
        ].filter(event => event.rawDate); // Filter out events with no raw date

        // Sort events chronologically
        // trackingEvents.sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));

        // Determine evidence type
        if (order.shipment?.evidence_drive_id) {
          setEvidenceType('pdf');
        } else if (order.shipment?.photos_drive_ids && order.shipment.photos_drive_ids.length > 0) {
          setEvidenceType('photos');
        } else {
          setEvidenceType(null);
        }

        // Calcular piezas totales
        const totalPieces = order.productos.reduce((sum, producto) => sum + producto.product_qt, 0);

        // Set order details state
        setOrderDetails({
          Order_number: order.DO,
          associatedShipment: order.metrics.shipment_number,
          customerPO: order.Customer_PO,
          deliveryDate: order.metrics.order_Delivery || "No entregado",
          vendor: order.customer,
          origin: order.Origin,
          destination: `${order.City}, ${order.State}`,
          creationDate: order.Created_dt,
          pieces: totalPieces,
          status: order.Order_status,
          substatus: order.order_Substatus,
          substatus_reentrega: order.substatus_reentrega,
          piecesDelivered: totalPieces - totalPiezasRechazadasTodas,
          rejections: rejections,
          shipments: shipments,
          productos: order.productos,
          photo_operador: order.metrics.photo_operador,
          pod_time: order.metrics.order_Podtime,
          Truck: order.shipment?.truck || 'No disponible',
          signoff_user: order.signoff_user,
          signoff_date: order.signoff_date,
          last_longitude: order.shipment?.last_longitude ?? 0,
          last_latitude: order.shipment?.last_latitude ?? 0,
          last_location_text: order.shipment?.last_location_text ?? order.shipment?.address ?? '',
        });

        setTrackingEvents(trackingEvents);
        // Actualizar validación automáticamente
        if (order.signoff_user && order.signoff_date) {
          setValidated(true);
          setValidationInfo({
            user: order.signoff_user,
            timestamp: order.signoff_date
          });
        } else {
          setValidated(false);
          setValidationInfo(null);
        }
      } else {
        setOrderDetails(null);
        setValidated(false);
        setValidationInfo(null);
        alert("No se encontró la orden.");
      }
    } catch (error) {
      console.error("Error al buscar la orden:", error);
      alert("Hubo un problema al buscar la orden.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    if (searchTerm) {
      // Convertir a mayúsculas para que la búsqueda no distinga mayúsculas/minúsculas
      const normalizedSearch = searchTerm.toUpperCase();
      // Update URL with the search term without reloading the page
      const url = new URL(window.location);
      url.searchParams.set('do', normalizedSearch);
      window.history.pushState({}, '', url);
      fetchOrderDetails(normalizedSearch);
    }
  };

  // Fetch products and check for URL parameters on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/productos`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching Products:', error);
      }
    };
    
    fetchProducts();
    
    // Parse URL query parameters
    const queryParams = new URLSearchParams(window.location.search);
    const doParam = queryParams.get('do');
    
    if (doParam) {
      setSearchTerm(doParam.toUpperCase());
      // Delayed slightly to ensure products are loaded
      setTimeout(() => {
        fetchOrderDetails(doParam.toUpperCase());
      }, 100);
    }
    
    // Add popstate event listener for browser navigation
    const handlePopState = () => {
      const newParams = new URLSearchParams(window.location.search);
      const newDoParam = newParams.get('do');
      
      if (newDoParam) {
        setSearchTerm(newDoParam.toUpperCase());
        fetchOrderDetails(newDoParam.toUpperCase());
      } else {
        setSearchTerm('');
        setOrderDetails(null);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const toggleShowAllEvents = () => {
    setShowAllEvents((prev) => !prev);
  };

  const renderEvidence = () => {
    if (!orderDetails?.shipments?.[0]) return null;
    const shipment = orderDetails.shipments[0];

    if (evidenceType === 'pdf' && shipment.evidence_drive_id) {
      return (
        <div className="max-w-3xl mx-auto">
          <iframe
            src={`https://drive.google.com/file/d/${shipment.evidence_drive_id}/preview`}
            width="100%"
            height="500px"
            style={{ border: 'none' }}
            title="Evidencia de Embarque"
            className="rounded-lg shadow-md"
          ></iframe>
          <div className="mt-2 flex justify-end">
            <a
              href={`https://drive.google.com/uc?export=download&id=${shipment.evidence_drive_id}`}
              download
              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
            >
              <Download size={18} /> Descargar
            </a>
          </div>
        </div>
      );
    } else if (evidenceType === 'photos' && shipment.photos_drive_ids && shipment.photos_drive_ids.length > 0) {
      const settings = {
        dots: false, // Ensure dots are disabled
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        centerMode: false,
        lazyLoad: 'ondemand',
        adaptiveHeight: true,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        responsive: [
          {
            breakpoint: 768,
            settings: {
              arrows: true,
              dots: false, // Ensure dots are disabled in responsive mode
            }
          }
        ]
      };

      return (
        <div className="max-w-3xl mx-auto">
          <style jsx="true">{`
            /* Hide dots */
            .slick-dots {
              display: none !important;
            }
            
            /* Rest of existing styles... */
            .slick-prev, .slick-next {
              z-index: 10;
              width: 40px;
              height: 40px;
              background-color: rgba(59, 130, 246, 0.7);
              border-radius: 50%;
              transition: background-color 0.2s;
            }
            
            .slick-prev:hover, .slick-next:hover {
              background-color: rgba(37, 99, 235, 0.9);
            }
            
            .slick-prev {
              left: 10px;
            }
            
            .slick-next {
              right: 10px;
            }
            
            .slick-prev:before, .slick-next:before {
              font-size: 24px;
              color: white;
            }
            
            @media (max-width: 640px) {
              .slick-prev {
                left: 5px;
              }
              .slick-next {
                right: 5px;
              }
              .slick-prev:before, .slick-next:before {
                font-size: 20px;
              }
            }
          `}</style>

          <div className="slider-container relative">
            <Slider {...settings}>
              {shipment.photos_drive_ids.map((photoId, index) => (
                <div key={index} className="p-2">
                  <div className="relative aspect-[4/3] bg-gray-100 rounded-lg">
                    <iframe
                      src={`https://drive.google.com/file/d/${photoId}/preview`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none', minHeight: '400px' }}
                      title={`Foto ${index + 1}`}
                      className="rounded-lg shadow-md"
                      allowFullScreen
                    />
                    <div className="absolute top-2 right-2 z-10">
                      <a
                        href={`https://drive.google.com/uc?export=download&id=${photoId}`}
                        download
                        className="inline-flex items-center gap-1 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                      >
                        <Download size={18} className="shrink-0" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          <div className="mt-6 text-center text-gray-500">
            {shipment.photos_drive_ids.length} foto(s) disponible(s)
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom arrow components for the Slider
  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'block' }}
        onClick={onClick}
        aria-label="Previous"
      />
    );
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{ ...style, display: 'block' }}
        onClick={onClick}
        aria-label="Next"
      />
    );
  };

  // Function to check if evidence exists
  const hasEvidence = () => {
    if (!orderDetails?.shipments?.[0]) return false;
    const shipment = orderDetails.shipments[0];
    
    return (
      (evidenceType === 'pdf' && shipment.evidence_drive_id) || 
      (evidenceType === 'photos' && shipment.photos_drive_ids && shipment.photos_drive_ids.length > 0)
    );
  };

  // Función para validar la orden
  const handleValidateOrder = async () => {
    if (!orderDetails?.Order_number) return;
    try {
      if (!VALIDATION_ELIGIBLE_STATUSES.includes(orderDetails.status)) {
        toast.error('Esta orden no puede ser validada porque no está en estado ENTREGADO o FINALIZADO', {
          position: "top-right",
          autoClose: 5000
        });
        return;
      }
      const toastId = toast.loading("Validando orden...", {
        position: "top-right"
      });
      const response = await api.post(`/api/v1/orders/validate`, { order_number: orderDetails.Order_number });
      setValidated(true);
      setValidationInfo({
        timestamp: response.data.timestamp || new Date().toISOString(),
        user: response.data.user || 'Usuario actual'
      });
      toast.update(toastId, {
        render: `La orden ${orderDetails.Order_number} ha sido validada exitosamente.`,
        type: "success",
        isLoading: false,
        autoClose: 5000
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al validar la orden. Por favor, inténtelo de nuevo.';
      toast.error(`No se pudo validar la orden: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  // Función para geocodificar dirección destino usando Nominatim
  const geocodeAddress = async (address, city, state) => {
    const query = encodeURIComponent(`${address ? address + ', ' : ''}${city}, ${state}, México`);
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`;
    const response = await fetch(url);
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon)
      };
    }
    return null;
  };  const getETAfromOSRM = async (origin, destination) => {
    try {
      const coordinates = `${origin.lon},${origin.lat};${destination.lon},${destination.lat}`;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/route/v1/driving/${coordinates}?overview=false`);
      const data = await response.json();
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
        return {
          duration: data.routes[0].duration, // en segundos
          distance: data.routes[0].distance  // en metros
        };
      }
      return null;
    } catch (error) {
      console.error('Error al obtener ETA:', error);
      return null;
    }
  };

  // useEffect para calcular ETA cuando se cargue la orden y tenga ubicación y destino
  useEffect(() => {
    const fetchEta = async () => {
      setEta(null);
      setLoadingEta(false);
      if (!orderDetails) return;
      const { last_latitude, last_longitude, destination, last_longitude: lon, last_latitude: lat } = orderDetails;
      if (!lat || !lon || lat === 0 || lon === 0) return;
      // destination: "CIUDAD, ESTADO"
      let address = orderDetails.last_location_text || orderDetails.address || '';
      let city = '';
      let state = '';
      if (orderDetails.destination) {
        const destParts = orderDetails.destination.split(',');
        city = destParts[0]?.trim() || '';
        state = destParts[1]?.trim() || '';
      }
      setLoadingEta(true);
      // Geocodificar destino
      const destCoords = await geocodeAddress(address, city, state);
      if (!destCoords) {
        setLoadingEta(false);
        return;
      }
      // Llamar a OSRM
      const etaData = await getETAfromOSRM({ lat, lon }, destCoords);
      if (etaData && etaData.duration) {
        setEta(Math.round(etaData.duration / 60)); // minutos
      }
      setLoadingEta(false);
    };
    fetchEta();
  }, [orderDetails]);

  const lastLongitude = orderDetails?.last_longitude ?? 0;
  const lastLatitude = orderDetails?.last_latitude ?? 0;
  const lastLocationText = orderDetails?.last_location_text ?? '';

  return (
    <div className="flex flex-col md:flex-row bg-gray-50 relative">
      {/* Sidebar with fixed positioning and higher z-index */}
      <div className="sticky top-0 h-screen z-20">
        <Sidebar
          isOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          onLogout={handleLogout}
        />
      </div>

      <div
        className={`transition-all duration-300 flex-1 ${
          sidebarOpen ? 'md:ml-64' : 'md:ml-16'
        } min-h-screen z-10`}
      >
        {/* Header with brand and search */}
        <div className="bg-gradient-to-r from-sky-800 to-blue-600 text-white p-4 md:p-6 shadow-lg sticky top-0 z-10">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <img src={logoamerisa} alt="Logo" className="h-8 md:h-10" />
                <h1 className="text-xl md:text-2xl font-bold">Seguimiento de Órdenes</h1>
              </div>
              <div className="relative w-full max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Número de orden (DO)"
                    className="w-full p-2 md:p-3 pl-4 md:pl-5 pr-16 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <button
                    onClick={handleSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white text-blue-600 px-3 md:px-4 py-1 md:py-1.5 rounded-md hover:bg-blue-50 transition-colors duration-200 font-medium text-sm md:text-base"
                  >
                    Buscar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
          {isLoading ? (
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando información de la orden...</p>
            </div>
          ) : orderDetails ? (
            <div className="space-y-4 md:space-y-8">
              {/* Order Summary Card */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-blue-600 to-sky-500 px-4 md:px-6 py-3 md:py-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <h2 className="text-lg md:text-xl font-bold text-white">Orden: {orderDetails.Order_number}</h2>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white text-blue-700 self-start sm:self-auto">
                      {orderDetails.substatus_reentrega || orderDetails.substatus || 'Pendiente'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 p-4 md:p-6">
                  <div className="space-y-3 md:space-y-4 relative">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                      Datos Principales
                    </h3>
                    <div className="space-y-2">
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Cliente:</span>
                        <span className="font-medium">{orderDetails.vendor}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Orden de compra:</span>
                        <span className="font-medium">{orderDetails.customerPO}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Creación:</span>
                        <span className="font-medium">{formatDate(orderDetails.creationDate)}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Entrega:</span>
                        <span className="font-medium">{formatDate(orderDetails.deliveryDate)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 md:space-y-4">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Detalle</h3>
                    <div className="space-y-2">
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Origen:</span>
                        <span className="font-medium">{orderDetails.origin}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Destino:</span>
                        <span className="font-medium">{orderDetails.destination}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Embarque:</span>
                        <span className="font-medium">{orderDetails.associatedShipment}</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 md:space-y-4 sm:col-span-2 lg:col-span-1">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Seguimiento</h3>
                    <div className="space-y-2">
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Piezas totales:</span>
                        <span className="font-medium">{orderDetails.pieces}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Piezas entregadas:</span>
                        <span className="font-medium">
                          {orderDetails.status !== ORDER_STATUS.CLOSED ? 'Pendiente' : orderDetails.piecesDelivered}
                        </span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">Evidencia Fotografica:</span>
                        <span className="font-medium">{orderDetails.photo_operador ? formatDate(orderDetails.photo_operador) : 'No disponible'}</span>
                      </p>
                      <p className="flex flex-wrap justify-between text-sm md:text-base">
                        <span className="text-gray-600">POD cargado:</span>
                        <span className="font-medium">{orderDetails.pod_time ? formatDate(orderDetails.pod_time) : 'No disponible'}</span>
                      </p>
                    </div>
                  </div>
                </div>
                {/* Mensaje/botón de validación ocupando todo el ancho */}
                <div className="px-4 md:px-6 pb-4 flex justify-end w-full">
                  {validated || validationInfo ? (
                    <span className="bg-green-100 text-green-700 px-4 py-2 rounded font-semibold flex flex-col md:flex-row md:items-center gap-2 text-xs md:text-sm w-full md:w-auto justify-center md:justify-end">
                      <span>
                        Validada{validationInfo?.timestamp ? ` el ${formatDate(validationInfo.timestamp)}` : ''}
                      </span>
                      {validationInfo?.user && (
                        <span className="text-green-800 font-normal ml-1">por: <b>{validationInfo.user}</b></span>
                      )}
                    </span>
                  ) : (
                    <button
                      className={`bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded font-semibold transition-colors text-xs md:text-sm w-full md:w-auto ${!VALIDATION_ELIGIBLE_STATUSES.includes(orderDetails.status) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleValidateOrder}
                      disabled={!VALIDATION_ELIGIBLE_STATUSES.includes(orderDetails.status)}
                      title={!VALIDATION_ELIGIBLE_STATUSES.includes(orderDetails.status) ? 'Solo se pueden validar órdenes entregadas o finalizadas' : 'Validar esta orden'}
                    >
                      Validar
                    </button>
                  )}
                </div>
              </div>

              {/* Delivery Status & Timeline */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-xl shadow-md p-4 md:p-6 h-full">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Estado de Entrega</h3>
                    <div className="space-y-3 md:space-y-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${orderDetails.substatus === 'DELIVERED' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                        <p className="font-medium text-gray-800 text-sm md:text-base">
                          {orderDetails.substatus_reentrega || orderDetails.substatus || 'Pendiente'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-xl shadow-md p-4 md:p-6 overflow-hidden">
                    <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Seguimiento de Movimientos</h3>
                    <div className="overflow-x-auto">
                      {trackingEvents.length > 0 ? (
                        <TrackingTimeline
                          events={trackingEvents}
                          showAllEvents={showAllEvents}
                          toggleShowAllEvents={toggleShowAllEvents}
                          lastLongitude={lastLongitude}
                          lastLatitude={lastLatitude}
                          lastLocationText={lastLocationText}
                          eta={eta}
                          loadingEta={loadingEta}
                        />
                      ) : (
                        <p className="text-gray-500 text-sm md:text-base">No hay movimientos registrados.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rejections */}
              {orderDetails.rejections?.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-4 md:p-6 overflow-hidden">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Rechazos</h3>
                  <div className="pt-2 md:pt-3">
                    {orderDetails.rejections.map((rejection, index) => (
                      <div key={index} className="p-2 md:p-3 bg-gray-50 rounded-lg mb-2 border border-gray-200">
                        <p className="text-xs md:text-sm"><span className="font-medium">Producto:</span> {rejection.codigo_producto}</p>
                        <p className="text-xs md:text-sm"><span className="font-medium">Razón:</span> {rejection.reason}</p>
                        <p className="text-xs md:text-sm"><span className="font-medium">Piezas:</span> {rejection.pieces}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shipment Details */}
              {orderDetails.shipments?.length > 0 ? (
                <div className="bg-white rounded-xl shadow-md p-4 md:p-6 overflow-hidden">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Detalles de Embarque</h3>
                  <div className="pt-2 md:pt-3">
                    {orderDetails.shipments.map((shipment, index) => (
                      <div key={index} className="p-2 md:p-3 bg-gray-50 rounded-lg mb-2 border border-gray-200">
                        <p className="text-xs md:text-sm"><span className="font-medium">ID:</span> {shipment.reference}</p>
                        <p className="text-xs md:text-sm"><span className="font-medium">Truck:</span> {shipment.Truck}</p>
                        <p className="text-xs md:text-sm"><span className="font-medium">Estado:</span> {shipment.status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm md:text-base">No hay embarques registrados.</p>
              )}

              {/* Evidence Section (conditional) */}
              {hasEvidence() && (
                <div className="bg-white rounded-xl shadow-md p-4 md:p-6 overflow-hidden">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 text-center">
                    {evidenceType === 'pdf' ? 'Comprobante de Entrega (POD)' : 'Evidencia Fotográfica'}
                  </h3>
                  <div className="border-t border-gray-200 pt-3 md:pt-4">
                    {renderEvidence()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-6 md:p-10 text-center">
              <svg className="w-12 h-12 md:w-16 md:h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <p className="mt-2 text-sm md:text-base text-gray-500">Ingrese un número de orden para visualizar su estado y seguimiento en tiempo real.</p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Cliente;